import * as excelJS from "exceljs";
import {IGradeBase, IGrades, IGradesMerged, IMergeRecord, ISpecData, Order} from "./types";
import { DateTime } from "luxon";


export const loadExcelFile = async (file: File, workSheetSelector: (names: string[]) => Promise<string | null>): Promise<IGrades> => {
  const grades: IGrades = {};
  const workbook = new excelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  const buffer = new Uint8Array(arrayBuffer) as any;
  await workbook.xlsx.load(buffer);
  const worksheetNames = workbook.worksheets.map((sheet) => sheet.name);
  let worksheetName: string | null = worksheetNames[0];

  if (workSheetSelector && worksheetNames.length > 1) {
    worksheetName = await workSheetSelector(worksheetNames); // Prompt user to select a worksheet

    if (!worksheetName) {

      return {}; // User cancelled the selection
    }
  }

  const worksheet = workbook.getWorksheet(worksheetName)!; // New Worksheet

  for (let rowIndex = 2; rowIndex <= worksheet.actualRowCount; rowIndex++) {
    const row = worksheet.getRow(rowIndex);
    const cellValue = row.getCell(1).value;

    if (typeof cellValue !== 'string'){

      continue; // Skip rows where the first cell is not a string
    }

    const recipeCellValue = row.getCell(2).value!;

    grades[cellValue.toString()] = {
      grade: cellValue.toString(),
      recipe: recipeCellValue.toString(), // Convert to string or default to empty
      specData: null, // Config is not provided in the Excel file
      elements:  {
        dolo: row.getCell(3).value ? Number(row.getCell(3).value) : 0, // Convert to number or default to 0
        lime: row.getCell(4).value ? Number(row.getCell(4).value) : 0 // Convert to number or default to 0
      }
    }
  }

  return grades;
}

export const exportGradesToExcel = async (grades: IGradeBase[]): Promise<excelJS.Buffer> => {
  const workbook = new excelJS.Workbook();
  const worksheet = workbook.addWorksheet('Grades');

  // Add header row
  worksheet.addRow(['Grade', 'Recipe', 'Dolo', 'Lime']);

  // Add data rows
  for (const grade of grades) {
    worksheet.addRow([
      grade.grade,
      grade.recipe,
      grade.elements.dolo,
      grade.elements.lime
    ]);
  }

  // Generate buffer
  const buffer = await workbook.xlsx.writeBuffer();
  return buffer as excelJS.Buffer;
}

export const mergeLimeGrades = (gradesDb: IGrades, gradesFile: IGrades | null): IMergeRecord[] => {
  const mergedGrades: IGradesMerged = {};

  for (const key in gradesDb) {
    mergedGrades[key] = {
      checked: !!gradesFile,
      status: gradesFile ? "DELETE" : "NONE",
      grade: gradesDb[key].grade,
      recipe: gradesDb[key].recipe,
      file: null,
      db: gradesDb[key]!
    };
  }

  for (const key in gradesFile) {

    if (mergedGrades[key]) {
      mergedGrades[key].file = gradesFile[key]!;
      evaluateStatus(mergedGrades[key]);
    } else {
      console.log(`Processing file grade: [${key}]`, mergedGrades);
      mergedGrades[key] = {
        checked: true,
        status: "ADD",
        file: gradesFile[key]!,
        grade: gradesFile[key].grade,
        recipe: gradesFile[key].recipe,
        db: null
      };
    }
  }

  return Object.values(mergedGrades);
}

const evaluateStatus = (record: IMergeRecord) => {
  for (const key in record.file!.elements) {
    if (record.file?.elements[key] !== record.db?.elements[key]) {
      record.checked = true;
      record.status = "UPDATE";

      return;
    }
  }

  record.checked = false;
  record.status = "NONE"; // If no differences found, set status to NONE
};

export const descendingComparator = (a: IMergeRecord, b: IMergeRecord, orderBy: keyof IMergeRecord) => {
  if (b[orderBy]! < a[orderBy]!) {
    return -1;
  }
  if (b[orderBy]! > a[orderBy]!) {
    return 1;
  }
  return 0;
}


export const getComparator = (
    order: Order,
    orderBy: keyof IMergeRecord,
): (
    a: IMergeRecord,
    b: IMergeRecord,
) => number => {
  return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
}


export const logger = (...args: any[]): void => {
  console.log(`[${DateTime.now().toFormat("MM-dd HH:mm:ss")}]`, ...args);
};

export const loggerError = (...args: any[]): void => {
  console.error(`[${DateTime.now().toFormat("MM-dd HH:mm:ss")}]`, ...args);
};


export const convertToIGrades = (spec: ISpecData[]): IGrades => {
  const grades: IGrades = {};

  spec.forEach((item) => {
    grades[item.name] = {
      grade: item.name,
      recipe: "",
      specData: item,
      elements: {
        dolo: 0,
        lime: 0
      }
    }

    item.content?.fields.forEach((field) => {
      if (field.name === "RECIPE") {
        grades[item.name].recipe = field.value.toString();
      } else if (field.name === "DOLO") {
        grades[item.name].elements.dolo = Number(field.value);
      } else if (field.name === "HICAL") {
        grades[item.name].elements.lime = Number(field.value);
      }
    });
  });

  return grades;
}

export const hasSpaceOrSpecialCharAtStart = (input: string): boolean => {
  const regex = /^[\s!@#$%^&*(),.?":{}|<>]/; // Matches spaces or special characters at the start
  return regex.test(input);
};

export const hasSpaceOrSpecialCharAtEnd = (input: string): boolean => {
  const regex = /[\s!@#$%^&*(),.?":{}|<>]$/; // Matches spaces or special characters at the end
  return regex.test(input);
};